/*
**  rui-rating.js
**
**  rui-rating component
*/
import { LitElement, html, css } from 'lit-element';
import { ifDefined } from 'lit-html/directives/if-defined';
export class RuiRating extends LitElement {
    static get is() {
        return 'rui-rating';
    }
    static get properties() {
        return {
            starCount: { type: Number },
            rating: { type: Number },
            size: { type: String },
            isEditable: { type: Object },
            allowClear: { type: Object },
            isDisabled: { type: Object }
        };
    }
    constructor() {
        super();
        this.starCount = 5;
        this.rating = 0;
        this.size = 'small';
        this.isEditable = false;
        this.allowClear = false;
        this.isDisabled = false;
    }
    click(e) {
        e.preventDefault();
        e.stopPropagation();
        const clickTree = (e.composedPath() || []).filter(item => item.localName === 'button');
        if (clickTree.length > 0) {
            let ratingId = clickTree[0].getAttribute('data-rui-rating-id');
            this.setRating(ratingId);
        }
    }
    clearRating(e) {
        e.preventDefault();
        e.stopPropagation();
        this.setRating(0);
    }
    setRating(rating) {
        this.rating = rating;
    }
    render() {
        let iconSize = '16px';
        const size = this.size ? this.size.toLowerCase() : 'small';
        if (size === 'medium') {
            iconSize = '20px';
        }
        else if (size === 'large') {
            iconSize = '24px';
        }
        // Ensure star count is within allowed range
        let starCount = Math.min(Math.max(this.starCount, 0), 10);
        // Ensure rating is within allowed range
        let rating = Math.min(Math.max(this.rating, 0), starCount);
        // Build list of starCount number of icons for this rating value
        let ratingIcons = [];
        for (let i = 1; i <= starCount; i++) {
            let icon = 'rating-empty';
            if (rating >= i) {
                icon = 'rating-full';
            }
            else if (rating + 0.5 >= i) {
                icon = 'rating-half';
            }
            ratingIcons.push(icon);
        }
        return html `
            <div class="rui-rating" title="${ifDefined(!this.isEditable || this.isDisabled ? this.rating : undefined)}" aria-valuemin="0" attr.aria-valuemax="${this.starCount}" attr.aria-valuenow="${this.rating}">
                ${ratingIcons.map((iconType, index) => html ` 
                    ${this.isEditable ?
            html `<button ?disabled="${this.isDisabled}" data-rui-rating-id="${index + 1}" title="${ifDefined(this.isEditable && !this.isDisabled ? index + 1 : undefined)}" @click="${this.click}" @mouseenter="${this.buttonMouseover}" @mouseleave="${this.buttonMouseout}">
                            <i class="rui-icon rui-icon-rating-full rui-icon-context-rating-btn-bottom" style="width: 24px; height: 24px; font-size: 24px;" aria-hidden="true"></i>
                            <i class="rui-icon rui-icon-${iconType} rui-icon-context-rating-btn" style="width: 24px; height: 24px; font-size: 24px;" aria-hidden="true" ?disabled="${this.isDisabled}"></i>
                        </button>` :
            html `<rui-icon iconType="${iconType}" context="rating" size="${iconSize}"></rui-icon>`}
                `)}
                ${this.isEditable && this.allowClear && !this.isDisabled ? html `<button title="Clear rating" @click="${this.clearRating}"><i class="rui-icon rui-icon-clear rui-icon-context-rating-btn" style="width: 24px; height: 24px; font-size: 24px;" aria-hidden="true"></i></button>` : ''}
            </div>
        `;
    }
    static get styles() {
        return css `
            .rui-rating {
                display: flex;
            }
            
            .rui-rating button {
                background: transparent;
                border: 0;
                height: 24px;
                margin: 0 2px;
                padding: 0;
                position: relative;
                width: 24px;
            }
            
            .rui-rating button:not([disabled]) {
                cursor: pointer;
            }

            .rui-icon-rating-empty:before { content: "\\2606"; }
            .rui-icon-rating-full:before { content: "\\2605"; }
            .rui-icon-rating-half:before { content: "\\e919"; }
            .rui-icon-clear:before { content: "\\1f5d9"; }

            .rui-icon {
                font-family: "JHAIcons";
                font-style: normal;
                font-weight: normal;
                font-variant: normal;
                text-transform: none;
                line-height: 1;
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
                display: inline-block;
            }

            .rui-rating button .rui-icon {
                left: 0;
                position: absolute;
                top: 0;
            }

            .rui-icon-context-rating {
                color: var(--text-regular, #181830);
                width: 14px;
                height: 14px;
                font-size: 14px;
            }

            .rui-icon-context-rating-btn {
                color: var(--btn-primary-bg, #3878ab);
                width: 24px;
                height: 24px;
                font-size: 24px;
            }

            button:not([disabled]):hover .rui-icon-context-rating-btn.rui-icon-rating-full, button:not([disabled]):hover .rui-icon-context-rating-btn.rui-icon-rating-empty {
                color: var(--btn-primary-bg-hover, #2e628c);
            }

            .rui-icon-context-rating-btn-bottom {
                color: var(--content-primary-bg, #fff);
                width: 24px;
                height: 24px;
                font-size: 24px;
            }

            button:not([disabled]):hover .rui-icon-context-rating-btn-bottom {
                color: var(--btn-secondary-bg-hover, #f2f2f5);
            }

            .rui-rating button[disabled] {
                opacity: .5;
            }
        `;
    }
}
customElements.get(RuiRating.is) || customElements.define(RuiRating.is, RuiRating);
